import { Injectable, inject, signal } from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { BasePaginationService } from '@components/pagination/pagination.type'
import {
  ArchiveCompanyAndUnlinkServiceProviderGQL,
  CompaniesPaginatedGQL,
  CompaniesPaginatedQuery,
  CompanyInput,
  CompanyOrderByInput,
  GetCompanyByIdGQL,
  GetCompanyByIdToDetailsGQL,
  GetCompanyWithStatsByIdGQL,
  PageInfo,
  UpdateCompanyGQL
} from '@generated/sammathnaur.graphql'
import { Subject, firstValueFrom, map } from 'rxjs'
import type { Apollo } from 'apollo-angular'

@Injectable({
  providedIn: 'root',
})
export class CompaniesService implements BasePaginationService {
  public search$: Subject<void> = new Subject()

  public readonly filterForm = new FormGroup({
    nameContains: new FormControl(''),
    archived: new FormControl(false as 'all' | false | true),
  })

  public orderBy = signal<CompanyOrderByInput>(CompanyOrderByInput.NameAsc)
  public limit = signal<number>(25)
  public offset = signal<number>(0)
  public totalCount = signal<number>(0)
  public pageInfo = signal<PageInfo | undefined>(undefined)
  public isLoading = signal<boolean>(false)

  private readonly companiesPageGQL = inject(CompaniesPaginatedGQL)
  private readonly getCompanyWithStatsByIdGQL = inject(GetCompanyWithStatsByIdGQL)
  private readonly getCompanyByIdGQL = inject(GetCompanyByIdGQL)
  private readonly getCompanyByIdToDetailsGQL = inject(GetCompanyByIdToDetailsGQL)
  private readonly updateCompanyGQL = inject(UpdateCompanyGQL)
  private readonly archiveCompanyGQL = inject(ArchiveCompanyAndUnlinkServiceProviderGQL)

  public async searchPage(): Promise<Apollo.QueryResult<CompaniesPaginatedQuery>> {
    const { nameContains, archived } = this.filterForm.value

    const observable$ = this.companiesPageGQL.fetch({
      variables: {
        where: {
          name_contains: nameContains,
          archived: archived === 'all' ? undefined : archived,
        },
        limit: this.limit(),
        offset: this.offset(),
        orderBy: this.orderBy(),
      },
    })

    return firstValueFrom(observable$)
  }

  public async getCompanies({ limit, offset, nameContains, orderBy }: { limit?: number, offset?: number, nameContains?: string, orderBy?: CompanyOrderByInput }) {
    return firstValueFrom(
      this.companiesPageGQL
        .fetch({
          variables: {
            where: {
              archived: false,
              name_contains: nameContains,
            },
            limit: limit ?? 25,
            offset: offset ?? 0,
            orderBy: orderBy || CompanyOrderByInput.NameAsc,
          },
        })
        .pipe(map((result) => result.data?.companies)),
    )
  }

  public async getCompanyById(companyId: string) {
    return firstValueFrom(
      this.getCompanyByIdGQL
        .fetch({
          variables: { id: companyId },
        })
        .pipe(map((result) => result.data?.company)),
    )
  }

  public async getCompanyByIdToDetails(companyId: string) {
    return firstValueFrom(
      this.getCompanyByIdToDetailsGQL
        .fetch({ variables: { id: companyId }, fetchPolicy: 'network-only' })
        .pipe(map((result) => result.data?.company)),
    )
  }

  public async getCompanyWithStatsById(companyId: string) {
    return firstValueFrom(
      this.getCompanyWithStatsByIdGQL
        .fetch({
          variables: { id: companyId },
        })
        .pipe(map((result) => result.data?.company)),
    )
  }

  public async updateCompany(input: { id: string, input: CompanyInput }) {
    return firstValueFrom(
      this.updateCompanyGQL
        .mutate({ variables: input })
        .pipe(map((result) => result.data?.updateCompany)),
    )
  }

  public async archiveCompanyAndUnlink(companyId: string) {
    return firstValueFrom(
      this.archiveCompanyGQL
        .mutate({ variables: { companyId } })
        .pipe(map((result) => result.data?.archiveCompanyAndUnlinkServiceProvider)),
    )
  }
}
